import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2, Mail, Sparkles } from '@/components/ui/icons';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SocialAuthButtons } from '@/components/auth/SocialAuthButtons';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { resolveLoginIdentifier } from '@/lib/defaultWorkspace';
import { AuthError } from '@supabase/supabase-js';

type Mode = 'signup' | 'login';

interface AuthPopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Which form to show first. Defaults to 'signup'. */
  initialMode?: Mode;
  /** Headline (defaults per mode). */
  title?: string;
  /** Sub-line under the headline (defaults per mode). */
  description?: string;
  /**
   * "Add another account" mode (#297). Runs a fresh sign-in / sign-up instead of
   * converting the current anonymous session — the current account is already
   * stashed in the multi-account store, and we want a SEPARATE account, not an
   * in-place upgrade of the guest.
   */
  addAccount?: boolean;
}

/**
 * Inline signup/login popup so a guest can convert without leaving their
 * prompt (email/password resolves in place; OAuth returns to the current
 * route). Full-page /signin and /signup routes remain for deep links and
 * magic-link/password-reset flows.
 */
export function AuthPopup({
  open,
  onOpenChange,
  initialMode = 'signup',
  title,
  description,
  addAccount = false,
}: AuthPopupProps) {
  const [mode, setMode] = useState<Mode>(initialMode);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verifyEmail, setVerifyEmail] = useState<string | null>(null);

  const { signIn, signUp, convertAnonymousAccount, user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // Return to the current route after the OAuth round-trip, not the homepage.
  const redirectPath = location.pathname === '/' ? '/' : location.pathname;

  const switchMode = (next: Mode) => {
    setMode(next);
    setError(null);
  };

  const close = (next: boolean) => {
    if (!next) {
      // Reset transient state so a re-open starts clean.
      setError(null);
      setVerifyEmail(null);
      setPassword('');
    }
    onOpenChange(next);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      if (mode === 'signup') {
        if (!name.trim()) {
          setError('Please enter your name.');
          return;
        }
        // Upgrade a guest's anonymous session in place so their work carries over
        // (same user_id). In "add another account" mode mint a separate, switchable
        // account instead — never convert the current session (#297).
        if (user?.is_anonymous && !addAccount) {
          await convertAnonymousAccount(email, password, name);
        } else {
          await signUp(email, password, name);
        }
        // Email signups require verification before the account is permanent, so
        // we can't just close — surface the "check your email" state in place.
        setVerifyEmail(email);
      } else {
        // Username (e.g. the demo curator) resolves to its email.
        await signIn(resolveLoginIdentifier(email), password);
        // onAuthStateChange updates the session; the gate that opened this popup
        // re-evaluates and closes. Dismiss the popup ourselves too.
        toast({ title: 'Signed in', description: 'Welcome back!' });
        close(false);
      }
    } catch (err) {
      const message =
        err instanceof AuthError
          ? err.message === 'Invalid login credentials'
            ? 'Invalid email or password.'
            : err.message
          : err instanceof Error
            ? err.message
            : 'Something went wrong.';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  // "Check your email" confirmation after an email signup.
  if (verifyEmail) {
    return (
      <Dialog open={open} onOpenChange={close}>
        <DialogContent className="w-[95vw] max-w-md border-telekinesis-neutral-800 bg-telekinesis-bg-secondary-dark p-8 text-telekinesis-neutral-10 sm:rounded-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl font-semibold">
              <Mail className="h-5 w-5 text-telekinesis-gold" />
              Check your email
            </DialogTitle>
            <DialogDescription className="text-sm text-telekinesis-neutral-400">
              We sent a verification link to{' '}
              <span className="font-medium text-telekinesis-neutral-10">
                {verifyEmail}
              </span>
              . Confirm it, then sign in to start generating.
            </DialogDescription>
          </DialogHeader>
          <Button
            onClick={() => {
              setVerifyEmail(null);
              switchMode('login');
            }}
            className="mt-2 h-10 w-full rounded-full bg-telekinesis-neutral-10 text-sm font-medium text-telekinesis-bg-dark [@media(hover:hover)]:hover:bg-white"
          >
            Back to sign in
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  const heading =
    title ??
    (mode === 'signup' ? 'Sign up to start generating' : 'Welcome back');
  const sub =
    description ??
    (mode === 'signup'
      ? "Create a free account to generate 3D models — you'll get free credits the moment you sign up."
      : 'Sign in to keep building.');

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="w-[95vw] max-w-md border-telekinesis-neutral-800 bg-telekinesis-bg-secondary-dark p-8 text-telekinesis-neutral-10 sm:rounded-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-semibold">
            <Sparkles className="h-5 w-5 text-telekinesis-gold" />
            {heading}
          </DialogTitle>
          <DialogDescription className="text-sm text-telekinesis-neutral-400">
            {sub}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 flex flex-col gap-4">
          <SocialAuthButtons redirectPath={redirectPath} />

          <div className="relative flex items-center gap-3">
            <div className="h-px flex-1 bg-telekinesis-neutral-800" />
            <span className="text-xs text-telekinesis-neutral-500">or</span>
            <div className="h-px flex-1 bg-telekinesis-neutral-800" />
          </div>

          {error && (
            <div className="rounded-md bg-[#5C1108]/50 p-3 text-sm text-[#F5A89E]">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            {mode === 'signup' && (
              <div className="space-y-1.5">
                <Label htmlFor="authpopup-name">Name</Label>
                <Input
                  id="authpopup-name"
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="border-telekinesis-neutral-800 bg-telekinesis-bg-dark text-telekinesis-neutral-10 placeholder:text-telekinesis-neutral-500 max-[430px]:text-base"
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="authpopup-email">
                {mode === 'login' ? 'Email or username' : 'Email'}
              </Label>
              <Input
                id="authpopup-email"
                type={mode === 'login' ? 'text' : 'email'}
                placeholder={
                  mode === 'login' ? 'Email or username' : 'you@example.com'
                }
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="border-telekinesis-neutral-800 bg-telekinesis-bg-dark text-telekinesis-neutral-10 placeholder:text-telekinesis-neutral-500 max-[430px]:text-base"
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="authpopup-password">Password</Label>
                {mode === 'login' && (
                  <button
                    type="button"
                    onClick={() => {
                      close(false);
                      navigate('/reset-password');
                    }}
                    className="text-xs text-telekinesis-neutral-400 transition-colors hover:text-telekinesis-neutral-10"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <Input
                id="authpopup-password"
                type="password"
                placeholder={
                  mode === 'signup' ? 'At least 6 characters' : 'Password'
                }
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="border-telekinesis-neutral-800 bg-telekinesis-bg-dark text-telekinesis-neutral-10 placeholder:text-telekinesis-neutral-500 max-[430px]:text-base"
              />
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="mt-1 h-10 w-full rounded-full bg-telekinesis-neutral-10 text-sm font-medium text-telekinesis-bg-dark [@media(hover:hover)]:hover:bg-white"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {mode === 'signup' ? 'Creating account…' : 'Signing in…'}
                </>
              ) : mode === 'signup' ? (
                'Create free account'
              ) : (
                'Sign in'
              )}
            </Button>
          </form>

          <div className="text-center text-sm text-telekinesis-neutral-400">
            {mode === 'signup' ? (
              <>
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => switchMode('login')}
                  className="text-telekinesis-neutral-10 underline-offset-2 hover:underline"
                >
                  Log in
                </button>
              </>
            ) : (
              <>
                New here?{' '}
                <button
                  type="button"
                  onClick={() => switchMode('signup')}
                  className="text-telekinesis-neutral-10 underline-offset-2 hover:underline"
                >
                  Create an account
                </button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
